import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Candidate } from '@/types/candidate';

interface UseDuplicateCandidatesReturn {
	duplicates: Candidate[];
	isLoading: boolean;
	error: string | null;
	hasDuplicates: boolean;
	checkForDuplicates: () => Promise<void>;
}

/**
 * Validates if a LinkedIn URL is properly qualified
 * Returns true if the URL contains a valid LinkedIn profile pattern
 */
function isValidLinkedInUrl(url: string | null | undefined): boolean {
	if (!url?.trim()) return false;
	
	const trimmedUrl = url.trim().toLowerCase();
	
	// Check if it's just the word "linkedin" or similar invalid values
	if (trimmedUrl === 'linkedin' || trimmedUrl === 'linked in' || trimmedUrl.length < 10) {
		return false;
	}
	
	// Check if it contains linkedin.com domain
	if (!trimmedUrl.includes('linkedin.com')) {
		return false;
	}
	
	// Check if it has a valid profile pattern (/in/ or /pub/)
	if (!trimmedUrl.includes('/in/') && !trimmedUrl.includes('/pub/')) {
		return false;
	}
	
	// Extract the profile identifier
	const profileId = trimmedUrl
		.replace(/https?:\/\/(www\.)?linkedin\.com\/(in|pub)\//, '')
		.replace(/\/$/, '')
		.split('?')[0];
	
	// Profile ID should have at least 3 characters
	return profileId.length >= 3;
}

/**
 * Hook to detect duplicate candidates based on email, phone, or LinkedIn profile
 * Excludes the current candidate from the results
 */
export function useDuplicateCandidates(candidate: Candidate | null): UseDuplicateCandidatesReturn {
	const [duplicates, setDuplicates] = useState<Candidate[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const checkForDuplicates = async () => {
		if (!candidate || !candidate.id) {
			setDuplicates([]);
			return;
		}

		setIsLoading(true);
		setError(null);
		try {
			// Build query conditions for duplicate detection
			const conditions: string[] = [];

			// Check for email duplicates
			if (candidate.email?.trim()) {
				conditions.push(`email.ilike.%${candidate.email.trim()}%`);
			}

			// Check for phone duplicates (normalize phone numbers)
			if (candidate.phone?.trim()) {
				const normalizedPhone = candidate.phone.replace(/[\s\-\(\)\+]/g, '');
				conditions.push(`phone.ilike.%${normalizedPhone}%`);
			}

			// Check for LinkedIn profile duplicates (only if properly qualified)
			if (isValidLinkedInUrl(candidate.linkedin)) {
				// Extract LinkedIn username/ID from URL
				const linkedinId = candidate.linkedin!
					.replace(/https?:\/\/(www\.)?linkedin\.com\/(in|pub)\//, '')
					.replace(/\/$/, '')
					.split('?')[0]; // Remove query parameters

				if (linkedinId) {
					conditions.push(`linkedin.ilike.%${linkedinId}%`);
				}
			}

			if (conditions.length === 0) {
				setDuplicates([]);
				return;
			}

			// Query the database for potential duplicates using proper OR syntax
			const { data, error: queryError } = await supabase
				.from('candidates')
				.select('id, name, email, phone, function, skills, status, location, linkedin, created_at, updated_at, manager, match_score, ai_analysis, ai_science_analysis, ai_synthesis, custom_fields, unavailable_until')
				.neq('id', candidate.id) // Exclude the current candidate
				.or(conditions.join(','));			if (queryError) {
				throw queryError;
			}

			// Filter results to ensure they're actual duplicates
			const actualDuplicates = (data || []).filter((dup: Candidate) => {
				// Check email match
				if (candidate.email?.trim() && dup.email?.trim()) {
					const candidateEmail = candidate.email.toLowerCase().trim();
					const dupEmail = dup.email.toLowerCase().trim();
					if (candidateEmail === dupEmail) return true;
				}

				// Check phone match (normalize both numbers consistently)
				if (candidate.phone?.trim() && dup.phone?.trim()) {
					const candidatePhone = candidate.phone.replace(/[\s\-\(\)\+]/g, '');
					const dupPhone = dup.phone.replace(/[\s\-\(\)\+]/g, '');
					if (candidatePhone === dupPhone && candidatePhone.length > 0) return true;
				}

				// Check LinkedIn match (only if both URLs are properly qualified)
				if (isValidLinkedInUrl(candidate.linkedin) && isValidLinkedInUrl(dup.linkedin)) {
					const normalizeLinkdIn = (url: string) => 
						url.replace(/https?:\/\/(www\.)?linkedin\.com\/(in|pub)\//, '')
						   .replace(/\/$/, '')
						   .split('?')[0]
						   .toLowerCase();

					const candidateLinkedIn = normalizeLinkdIn(candidate.linkedin!);
					const dupLinkedIn = normalizeLinkdIn(dup.linkedin!);

					if (candidateLinkedIn === dupLinkedIn && candidateLinkedIn.length > 0) return true;
				}

				return false;
			});

			setDuplicates(actualDuplicates);
		} catch (err) {
			console.error('Error checking for duplicate candidates:', err);
			setError(err instanceof Error ? err.message : 'Failed to check for duplicates');
			setDuplicates([]);
		} finally {
			setIsLoading(false);
		}
	};

	// Check for duplicates when candidate changes
	useEffect(() => {
		checkForDuplicates();
	}, [candidate?.id, candidate?.email, candidate?.phone, candidate?.linkedin]);

	return {
		duplicates,
		isLoading,
		error,
		hasDuplicates: duplicates.length > 0,
		checkForDuplicates
	};
}
